import { useQuery } from '@tanstack/react-query';
import { contactKeys } from '@/lib/query-keys';
import { contactService } from '@/services/contact.service';

/**
 * The full contact list, for dropdowns.
 *
 * `useContacts()` is driven by the shared contact filter store — the same one
 * the Contacts list page writes to. A picker fed from it only ever sees
 * whatever page, search or filter the user last left on that screen, so a
 * document whose contact falls outside that slice renders an empty Select with
 * no matching option and the value looks lost. Pickers read this instead.
 *
 * Deliberately unfiltered, and it pages through everything: the dropdown has
 * to be able to resolve ANY stored contactId, not just the first page. The API
 * caps a page at 100 (PaginationDto @Max(100)) — asking for more in one go is
 * a 400, which is how this once rendered an empty picker on live.
 */
const PAGE_SIZE = 100;
/** Stop runaway paging if the API ever misreports totalPages. */
const MAX_PAGES = 50;

async function fetchAllContacts() {
  const first = await contactService.getAll({ page: 1, limit: PAGE_SIZE, sortBy: 'firstName', sortOrder: 'asc' });
  const all = [...(first.data ?? [])];
  const totalPages = Math.min(first.meta?.totalPages ?? 1, MAX_PAGES);
  for (let page = 2; page <= totalPages; page++) {
    const next = await contactService.getAll({ page, limit: PAGE_SIZE, sortBy: 'firstName', sortOrder: 'asc' });
    all.push(...(next.data ?? []));
  }
  return all;
}

export function useContactOptions() {
  const query = useQuery({
    queryKey: contactKeys.list({ scope: 'options' }),
    queryFn: fetchAllContacts,
    staleTime: 60_000,
  });

  return {
    contacts: query.data ?? [],
    isLoading: query.isLoading,
    /** True once the list is loaded — a picker should not judge a value missing before this. */
    isReady: !query.isLoading && !!query.data,
  };
}
